if (!customElements.get('product-page-nav')) {
  customElements.define('product-page-nav', class ProductPageNav extends HTMLElement {
    connectedCallback() {
      this._links = Array.from(this.querySelectorAll('.product-page-nav__link'));
      this._sections = this._links
        .map((link) => this._getTarget(link))
        .filter(Boolean);

      this._clickHandlers = new Map();
      this._links.forEach((link) => {
        const handler = (event) => this._onLinkClick(event, link);
        this._clickHandlers.set(link, handler);
        link.addEventListener('click', handler);
      });

      if ('IntersectionObserver' in window && this._sections.length) {
        this._activeObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this._setActive(entry.target.id);
          });
        }, { rootMargin: '-15% 0px -75% 0px' });
        this._sections.forEach((section) => this._activeObserver.observe(section));
      }

      this._sentinel = document.createElement('div');
      this._sentinel.setAttribute('aria-hidden', 'true');
      this._sentinel.style.cssText = 'height:1px;margin:0;padding:0;';
      this.before(this._sentinel);

      this._placeholder = document.createElement('div');
      this._placeholder.setAttribute('aria-hidden', 'true');
      this._placeholder.style.display = 'none';
      this.after(this._placeholder);

      this._initObserver = this._initObserver.bind(this);
      this._onResize = this._onResize.bind(this);

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this._initObserver, { once: true });
      } else {
        this._initObserver();
      }

      window.addEventListener('resize', this._onResize);
    }

    _getTarget(link) {
      const href = link.getAttribute('href');
      if (!href || !href.startsWith('#') || href.length === 1) return null;
      return document.querySelector(href);
    }

    _onLinkClick(event, link) {
      const target = this._getTarget(link);
      if (!target) return;

      event.preventDefault();
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
      target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
      this._setActive(target.id);
    }

    _getHeaderHeight() {
      const fallbackHeaderHeight = 140;
      const headerHeightStyle = getComputedStyle(document.documentElement)
        .getPropertyValue('--header-height')
        .trim();
      if (!headerHeightStyle) return fallbackHeaderHeight;

      const headerHeight = parseFloat(headerHeightStyle);
      if (Number.isNaN(headerHeight)) return fallbackHeaderHeight;

      // Dawn sets 1rem to 10px, so unresolved CSS custom properties need conversion.
      return headerHeightStyle.includes('rem') ? headerHeight * 10 : headerHeight;
    }

    _initObserver() {
      this._fixObserver?.disconnect();

      if (!('IntersectionObserver' in window) || !this._sentinel) return;

      const headerHeight = this._getHeaderHeight();

      this._fixObserver = new IntersectionObserver(([entry]) => {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < headerHeight;
        this.classList.toggle('product-page-nav--fixed', scrolledPast);
        this._placeholder.style.cssText = scrolledPast
          ? `display:block;height:${this.getBoundingClientRect().height}px;`
          : 'display:none;';
      }, {
        threshold: 0,
        rootMargin: `-${headerHeight}px 0px 0px 0px`,
      });

      this._fixObserver.observe(this._sentinel);
    }

    _onResize() {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this._initObserver(), 150);
    }

    _setActive(id) {
      this._links.forEach((link) => {
        link.classList.toggle('product-page-nav__link--active', link.getAttribute('href') === `#${id}`);
      });
    }

    disconnectedCallback() {
      this._activeObserver?.disconnect();
      this._fixObserver?.disconnect();
      this._clickHandlers?.forEach((handler, link) => link.removeEventListener('click', handler));
      window.removeEventListener('resize', this._onResize);
      clearTimeout(this._resizeTimer);
      this._sentinel?.remove();
      this._placeholder?.remove();
    }
  });
}
